import { SOLVE_AUTOFLOW_BASE_URL, SOLVE_V2_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import { SolveWidgetProduct } from 'src/types/types';
import {
  AutoflowToolResponse,
  CanvasResponse,
  Intent,
} from 'src/types/workflowBuilderAPITypes';

export interface CreateIntentRequest {
  is_on_deck?: boolean;
  name: string;
  parent_autoflow_intent_workflow_id?: string;
  product?: SolveWidgetProduct;
}

export interface UpdateIntentRequest {
  description?: string;
  intent_id: string;
  name: string;
}

export interface ClaimBuildingBlockRequest {
  intentWorkflowId: string;
}

export interface ClaimBuildingBlockResponse {
  intent_id: string | null;
  ready: boolean;
  workflow_id: string | null;
}

export interface PrepBuildingBlockRequest {
  intentWorkflowId: string;
}

export interface PrepBuildingBlockResponse {
  should_create: boolean;
}

const workflowBuilderIntentApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    checkBuildingBlockPrep: builder.query<
      PrepBuildingBlockResponse,
      PrepBuildingBlockRequest
    >({
      query: ({ intentWorkflowId }) => ({
        method: 'GET',
        url: `${SOLVE_AUTOFLOW_BASE_URL}/workflow-builder/${intentWorkflowId}/building-block/prep`,
      }),
    }),
    claimBuildingBlock: builder.mutation<
      ClaimBuildingBlockResponse,
      ClaimBuildingBlockRequest
    >({
      query: ({ intentWorkflowId }) => ({
        method: 'POST',
        url: `${SOLVE_AUTOFLOW_BASE_URL}/workflow-builder/${intentWorkflowId}/building-block/claim`,
      }),
    }),
    createIntent: builder.mutation<CanvasResponse, CreateIntentRequest>({
      invalidatesTags: ['Intents', 'Tools'],
      query: body => ({
        body: {
          handoff_configuration: {
            is_default_handoff: false,
            is_handoff: true,
          },
          phrases: [],
          ...body,
        },
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/workflow-builder/workflow`,
      }),
    }),
    updateIntent: builder.mutation<Intent, UpdateIntentRequest>({
      invalidatesTags: (_result, _error, { intent_id }) => [
        { id: intent_id, type: 'Intent' },
        'Intents',
        'Tools',
        'AutoflowWorkflow',
      ],
      async onQueryStarted(
        { description, intent_id, name },
        { dispatch, queryFulfilled },
      ) {
        const patches: Array<{ undo: () => void }> = [];

        // Optimistically update the getIntent cache
        try {
          const intentPatch = dispatch(
            dashboardApi.util.updateQueryData(
              'getIntent' as never,
              { intentDefinitionId: intent_id } as never,
              ((draft: Intent) => {
                draft.title = name;
                if (description !== undefined) {
                  draft.description = description;
                }
              }) as never,
            ),
          );
          patches.push(intentPatch);
        } catch {
          // Cache entry may not exist yet, skip
        }

        // Optimistically update all getAutoflowTools caches
        // We need to update all product variants since we don't know which one is cached
        const products: SolveWidgetProduct[] = [
          'workflow_builder',
          'interactive_email',
          'api',
          'slack',
          'voice',
          'playbook',
        ];

        products.forEach(product => {
          try {
            const toolsPatch = dispatch(
              dashboardApi.util.updateQueryData(
                'getAutoflowTools' as never,
                { product } as never,
                ((draft: AutoflowToolResponse) => {
                  const tool = draft.tools.find(
                    t => t.tool_id === intent_id && t.tool_type === 'workflow',
                  );
                  if (tool) {
                    tool.tool_name = name;
                  }
                }) as never,
              ),
            );
            patches.push(toolsPatch);
          } catch {
            // Cache entry may not exist for this product, skip
          }
        });

        try {
          await queryFulfilled;
        } catch {
          // Undo all optimistic updates on error
          patches.forEach(patch => patch.undo());
        }
      },
      query: ({ description, intent_id, name }) => ({
        body: {
          description,
          name,
          phrases: [],
        },
        method: 'PUT',
        params: { workflow_id: intent_id },
        url: `${SOLVE_V2_BASE_URL}/workflow-builder/intent/${intent_id}`,
      }),
    }),
  }),
});

export const {
  useCheckBuildingBlockPrepQuery,
  useClaimBuildingBlockMutation,
  useCreateIntentMutation,
  useLazyCheckBuildingBlockPrepQuery,
  useUpdateIntentMutation,
} = workflowBuilderIntentApi;
